import { Brand } from 'cpie-common';
import { Button, ButtonContainer, Heading, Layout, TileSelect, TileSelectGroup } from '@vwfs-bronson/bronson-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { dashboardPagePath } from '../../../navigation/paths';
import { ContractEndOptionsView } from '../enums';
import { useAnalyticsActionTracker } from '@cp-shared-15/frontend-ui';

export enum Option {
    RENEWAL = 'RENEWAL',
    REFINANCE = 'REFINANCE',
    CAR_RETURN = 'CAR_RETURN',
}
type Tile = {
    value: Option;
    icon: string;
    title: string;
    description: string;
};

export const OptionsOverview: React.FC<{
    handleSubmit: (contractEndOptionsView: ContractEndOptionsView) => void;
    brand?: Brand;
    header: React.ReactNode;
}> = ({ handleSubmit, brand, header }) => {
    const { t } = useTranslation('contract-end-options');
    const history = useHistory();

    const [selectedOption, setSelectedOption] = useState<Option | undefined>();

    const { onAction: onDealerData } = useAnalyticsActionTracker('onEndOfContractPcpDealerData');

    const showRenewalTile = brand === Brand.VW || brand === Brand.AUDI || brand === Brand.SEAT || brand === Brand.SKODA;

    const mainViewTiles: Tile[] = useMemo(() => {
        const renewalTile: Tile[] = showRenewalTile
            ? [
                  {
                      value: Option.RENEWAL,
                      icon: 'semantic-car',
                      title: t(`options-overview.options.renewal.title`),
                      description: t(`options-overview.options.renewal.description`),
                  },
              ]
            : [];

        return [
            ...renewalTile,
            {
                value: Option.REFINANCE,
                icon: 'semantic-accessoires',
                title: t(`options-overview.options.refinance.title`),
                description: t(`options-overview.options.refinance.description`),
            },
            {
                value: Option.CAR_RETURN,
                icon: 'semantic-reload',
                title: t(`options-overview.options.car-return.title`),
                description: t(`options-overview.options.car-return.description`),
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRenewalTile, t]);

    const handleBackClick = useCallback((): void => {
        history.push(dashboardPagePath());
    }, [history]);

    const handleSubmitClick = useCallback((): void => {
        if (!selectedOption) {
            return;
        }

        switch (selectedOption) {
            case Option.REFINANCE:
                onDealerData('Refinance');
                handleSubmit(ContractEndOptionsView.DEALER_INFORMATION);
                return;
            case Option.CAR_RETURN:
                onDealerData('Return the car');
                handleSubmit(ContractEndOptionsView.DEALER_INFORMATION);
                return;
            case Option.RENEWAL:
                handleSubmit(ContractEndOptionsView.RENEWAL_OPTIONS);
                return;
        }
    }, [selectedOption, onDealerData, handleSubmit]);

    const handleTileClick = useCallback((value: Option): void => {
        setSelectedOption(value);
    }, []);

    return (
        <Layout>
            <Layout.Item>
                <Heading className="u-mt" level={1}>
                    {t('options-overview.headline')}
                </Heading>
            </Layout.Item>
            <Layout.Item>{header}</Layout.Item>
            <Layout.Item>
                <Heading className="u-mt" level={3} id="options-overview-heading">
                    {t('options-overview.sub-headline')}
                </Heading>
                <p>{t('options-overview.description', { optionsCount: showRenewalTile ? 'three' : 'two' })}</p>
            </Layout.Item>
            <Layout.Item>
                <TileSelectGroup
                    title={{ referenceId: 'options-overview-heading' }}
                    key="MAIN"
                    layoutItemClassName={`u-1/${showRenewalTile ? 3 : 2} u-1/2@m u-1/1@s`}
                    className={'u-text-center'}
                    testId={'tile-select-group-main'}
                >
                    {mainViewTiles.map(({ value, title, description, icon }) => (
                        <TileSelect
                            key={value}
                            inputType="radio"
                            radioGroupName="MAIN"
                            onClick={(): void => handleTileClick(value)}
                            value={value}
                            title={title}
                            icon={icon}
                            testId={value}
                        >
                            {description}
                        </TileSelect>
                    ))}
                </TileSelectGroup>
            </Layout.Item>
            <Layout.Item>
                <ButtonContainer center className={'u-mt'}>
                    <Button secondary testId="backButton" onClick={handleBackClick}>
                        {t('translation:editable-section-nav.back')}
                    </Button>
                    <Button testId="submitButton" disabled={!selectedOption} onClick={handleSubmitClick}>
                        {t('translation:editable-section-nav.submit')}
                    </Button>
                </ButtonContainer>
            </Layout.Item>
        </Layout>
    );
};
